/**
 * Real, publicly available sources from recognised health bodies.
 * Every URL here was checked to load before being added. Do not add
 * a source without opening it first.
 */
export type Citation = { title: string; publisher: string; url: string };

const S = {
  nhsPtsd: {
    title: "Post-traumatic stress disorder (PTSD) — overview",
    publisher: "NHS",
    url: "https://www.nhs.uk/mental-health/conditions/post-traumatic-stress-disorder-ptsd/overview/",
  },
  nhsCptsd: {
    title: "Complex PTSD",
    publisher: "NHS",
    url: "https://www.nhs.uk/mental-health/conditions/post-traumatic-stress-disorder-ptsd/complex/",
  },
  nice: {
    title: "Post-traumatic stress disorder (guideline NG116)",
    publisher: "NICE",
    url: "https://www.nice.org.uk/guidance/ng116",
  },
  who: {
    title: "Post-traumatic stress disorder — fact sheet",
    publisher: "World Health Organization",
    url: "https://www.who.int/news-room/fact-sheets/detail/post-traumatic-stress-disorder",
  },
  nimhPtsd: {
    title: "Post-Traumatic Stress Disorder",
    publisher: "National Institute of Mental Health (NIMH)",
    url: "https://www.nimh.nih.gov/health/topics/post-traumatic-stress-disorder-ptsd",
  },
  nimhAnxiety: {
    title: "Anxiety Disorders",
    publisher: "National Institute of Mental Health (NIMH)",
    url: "https://www.nimh.nih.gov/health/topics/anxiety-disorders",
  },
  tip: {
    title: "Working definition of trauma-informed practice",
    publisher: "UK Government (OHID)",
    url: "https://www.gov.uk/government/publications/working-definition-of-trauma-informed-practice/working-definition-of-trauma-informed-practice",
  },
  va: {
    title: "National Center for PTSD",
    publisher: "U.S. Department of Veterans Affairs",
    url: "https://www.ptsd.va.gov/",
  },
  breathing: {
    title: "Breathing exercises for stress",
    publisher: "NHS",
    url: "https://www.nhs.uk/mental-health/self-help/guides-tools-and-activities/breathing-exercises-for-stress/",
  },
  gad: {
    title: "Generalised anxiety disorder in adults",
    publisher: "NHS",
    url: "https://www.nhs.uk/mental-health/conditions/generalised-anxiety-disorder/overview/",
  },
  sleep: {
    title: "Sleep problems — Every Mind Matters",
    publisher: "NHS",
    url: "https://www.nhs.uk/every-mind-matters/mental-health-issues/sleep/",
  },
  dv: {
    title: "Getting help for domestic abuse",
    publisher: "NHS",
    url: "https://www.nhs.uk/live-well/getting-help-for-domestic-violence/",
  },
  womensAid: {
    title: "Domestic abuse information and support",
    publisher: "Women’s Aid",
    url: "https://www.womensaid.org.uk/information-support/",
  },
  talking: {
    title: "NHS Talking Therapies",
    publisher: "NHS",
    url: "https://www.nhs.uk/mental-health/talking-therapies-medicine-treatments/talking-therapies-and-counselling/nhs-talking-therapies/",
  },
  fiveSteps: {
    title: "5 steps to mental wellbeing",
    publisher: "NHS",
    url: "https://www.nhs.uk/mental-health/self-help/guides-tools-and-activities/five-steps-to-mental-wellbeing/",
  },
  samaritans: { title: "Samaritans — 116 123", publisher: "Samaritans", url: "https://www.samaritans.org/" },
  lifeline: {
    title: "988 Suicide & Crisis Lifeline",
    publisher: "988 Lifeline (US)",
    url: "https://988lifeline.org/",
  },
} satisfies Record<string, Citation>;

const BY_CATEGORY: Record<string, Citation[]> = {
  "trauma-recovery": [S.nhsPtsd, S.who, S.nice, S.tip],
  cptsd: [S.nhsCptsd, S.nice, S.who, S.nimhPtsd],
  "nervous-system": [S.nimhPtsd, S.breathing, S.va],
  grounding: [S.breathing, S.nhsPtsd, S.va],
  "somatic-therapy": [S.nice, S.nhsPtsd, S.talking],
  sleep: [S.sleep, S.nhsPtsd],
  anxiety: [S.gad, S.nimhAnxiety, S.breathing],
  relationships: [S.dv, S.nhsCptsd, S.talking],
  attachment: [S.nhsCptsd, S.talking],
  "self-care": [S.fiveSteps, S.breathing],
};

/** Sources shown at the end of a blog article, chosen by topic. */
export function citationsForPost(category: string, slug: string): Citation[] {
  const list = [...(BY_CATEGORY[category] ?? [S.nhsPtsd, S.who])];
  if (/trauma-bond/i.test(slug) && !list.includes(S.womensAid)) list.unshift(S.womensAid);
  if (/cptsd|complex/i.test(slug) && !list.includes(S.nhsCptsd)) list.unshift(S.nhsCptsd);
  if (/sleep|nightmare/i.test(slug) && !list.includes(S.sleep)) list.push(S.sleep);
  if (/anxi|panic/i.test(slug) && !list.includes(S.gad)) list.push(S.gad);
  if (/abuse|narciss|coercive/i.test(slug) && !list.includes(S.dv)) list.push(S.dv);
  return list.slice(0, 5);
}

/** Sources attached to specific FAQ answers. */
export const FAQ_CITATIONS: Record<string, Citation[]> = {
  "what-is-rt": [S.tip, S.nhsPtsd],
  "is-therapy": [S.talking, S.nice],
  "who-for": [S.nhsPtsd, S.nhsCptsd],
  "trauma-informed": [S.tip, S.talking],
  crisis: [S.samaritans, S.lifeline],
  "ground-me": [S.breathing],
  "assessments-diagnostic": [S.nice, S.nhsPtsd],
};
